import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackFormSubmission, trackEvent, trackDownload } from '../utils/analytics';
import { calculateAgeFromDateString, formatDateForDisplay, formatDateForInput, getCurrentDateForInput } from '../utils/dateFormat';
import PartialPaymentModal from '../components/PartialPaymentModal';
import CashbackModal from '../components/CashbackModal';

const AffiliatedCustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    invoiceDate: getCurrentDateForInput(),
    dueDate: '',
    description: '',
    status: 'PAID',
    paymentDate: '',
    paymentMode: '',
    paymentType: 'FULL',
    paidAmount: '',
    notes: '',
    lineItems: []
  });
  const [invoiceType, setInvoiceType] = useState('PRODUCT'); // PRODUCT or PROGRAM
  const [products, setProducts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [currentLineItem, setCurrentLineItem] = useState({
    itemType: 'PRODUCT',
    itemId: '',
    quantity: 1
  });
  const [gstCalculations, setGstCalculations] = useState({
    subtotal: 0,
    totalCgst: 0,
    totalSgst: 0,
    totalDiscount: 0,
    totalAmount: 0,
    subtotalBeforeCashback: 0,
    programProductDiscount: 0,
    shippingSubtotal: 0,
    shippingCgst: 0,
    shippingSgst: 0,
    shippingTotal: 0
  });
  const [cashbackBalance, setCashbackBalance] = useState(null);
  const [appliedCashback, setAppliedCashback] = useState(0);
  const [customDiscountEnabled, setCustomDiscountEnabled] = useState(false);
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingCharges, setShippingCharges] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [showCashbackModal, setShowCashbackModal] = useState(false);
  const [userPrograms, setUserPrograms] = useState([]);
  const [userProgramsLoading, setUserProgramsLoading] = useState(false);
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [newProgram, setNewProgram] = useState({
    programId: '',
    startDate: getCurrentDateForInput(),
    endDate: '',
    notes: ''
  });
  const [programFormLoading, setProgramFormLoading] = useState(false);
  const [programFormError, setProgramFormError] = useState('');
  const [showProgramDetailsModal, setShowProgramDetailsModal] = useState(false);
  const [selectedProgramForDetails, setSelectedProgramForDetails] = useState(null);
  const [programDetailsComments, setProgramDetailsComments] = useState('');
  const [programDates, setProgramDates] = useState({startDate:'',endDate:''});
  const [programDateMessage, setProgramDateMessage] = useState('');
  const [programDetailsSaving, setProgramDetailsSaving] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedProgramAttendance, setSelectedProgramAttendance] = useState(null);
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [showStopProgramModal, setShowStopProgramModal] = useState(false);
  const [selectedProgramToStop, setSelectedProgramToStop] = useState(null);
  const [stopProgramComments, setStopProgramComments] = useState('');
  const [stopProgramLoading, setStopProgramLoading] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
    fetchInvoices();
    fetchProducts();
    fetchPrograms();
    fetchCompanyInfo();
    fetchCashbackBalance();
    fetchUserPrograms();
    
    // Track affiliated customer details page view
    trackFeatureUsage('Affiliated Customer Details', 'view');
    trackEvent('affiliated_customer_details_viewed', {
      event_category: 'Affiliated Customer Management',
      customer_id: customerId
    });
  }, [customerId]);

  useEffect(() => {
    // Calculate end date when program or start date changes
    if (newProgram.programId && newProgram.startDate) {
      const selectedProgram = programs.find(p => p.id === parseInt(newProgram.programId));
      if (selectedProgram && selectedProgram.days) {
        const startDate = new Date(newProgram.startDate);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + selectedProgram.days - 1);
        setNewProgram(prev => ({
          ...prev,
          endDate: formatDateForInput(endDate)
        }));
      }
    }
  }, [newProgram.programId, newProgram.startDate, programs]);

  useEffect(() => {
    // Recalculate GST when line items change
    calculateGstTotals();
  }, [newInvoice.lineItems, companyInfo]);

  useEffect(() => {
    // Recalculate GST when custom discount is toggled
    calculateGstTotals();
  }, [customDiscountEnabled, customDiscountAmount]);

  useEffect(() => {
    // Recalculate GST when shipping charges change
    calculateGstTotals();
  }, [shippingEnabled, shippingCharges]);

  useEffect(() => {
    // Recalculate invoice totals when cashback is applied or removed
    calculateGstTotals();
  }, [appliedCashback]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await axios.get(`/api/customers/${customerId}`);
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      setError('Failed to load customer details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCashbackBalance = async () => {
    try {
      const response = await axios.get(`/api/cashback/balance/${customerId}`);
      setCashbackBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch cashback balance:', error);
    }
  };

  const fetchUserPrograms = async () => {
    try {
      setUserProgramsLoading(true);
      const response = await axios.get(`/api/user-programs/${customerId}`);
      setUserPrograms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user programs:', error);
    } finally {
      setUserProgramsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products/all');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get('/api/products/programs');
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get('/api/company');
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  const calculateGstTotals = () => {
    const gstApplicable = Boolean(companyInfo?.gstinNumber?.trim());
    let subtotalBeforeCashback = 0;
    let totalCgstBeforeCashback = 0;
    let totalSgstBeforeCashback = 0;
    let programProductDiscount = 0;

    // Custom discount for program invoices
    const customDiscount = (invoiceType === 'PROGRAM' && customDiscountEnabled) ? 
      (parseFloat(customDiscountAmount) || 0) : 0;

    if (customDiscountEnabled && customDiscount > 0) {
      // For custom discount: Calculate total MRP, subtract custom discount, then calculate GST
      let totalMrp = 0;
      
      newInvoice.lineItems.forEach(lineItem => {
        const quantity = parseInt(lineItem.quantity) || 0;
        const selectedProgram = programs.find(p => p.id === lineItem.itemId);
        if (selectedProgram) {
          totalMrp += selectedProgram.programMrp * quantity;
        }
      });
      
      // Final amount after custom discount
      const finalAmountAfterDiscount = Math.max(0, totalMrp - customDiscount);
      
      // Calculate GST on final amount (18% for programs)
      const gstRate = 18;
      const cgstRate = gstRate / 2;
      const sgstRate = gstRate / 2;
      
      // Calculate base price (excluding GST) from final amount
      subtotalBeforeCashback = gstApplicable
        ? finalAmountAfterDiscount / (1 + (gstRate / 100))
        : finalAmountAfterDiscount;
      totalCgstBeforeCashback = gstApplicable ? (subtotalBeforeCashback * cgstRate) / 100 : 0;
      totalSgstBeforeCashback = gstApplicable ? (subtotalBeforeCashback * sgstRate) / 100 : 0;
      programProductDiscount = customDiscount;
    } else {
      // Normal calculation without custom discount
      newInvoice.lineItems.forEach(lineItem => {
        const itemType = lineItem.itemType;
        let unitPrice = parseFloat(lineItem.unitPrice) || 0;
        const quantity = parseInt(lineItem.quantity) || 0;
        
        let discountPercentage = 0;
        
        // For program invoices, apply slab discount if applicable
        if (itemType === 'PROGRAM') {
          const selectedProgram = programs.find(p => p.id === lineItem.itemId);
          if (selectedProgram && selectedProgram.discountApplicable && customer?.programDiscountPlan) {
            discountPercentage = parseFloat(customer.programDiscountPlan.discountPercentage) || 0;
          }
        }

        // Calculate discount
        const discountAmount = (unitPrice * quantity * discountPercentage) / 100;
        const discountedTotal = (unitPrice * quantity) - discountAmount;

        // Calculate GST rates based on item type
        const gstRate = itemType === 'PRODUCT' ? 5 : 18;
        const cgstRate = gstRate / 2;
        const sgstRate = gstRate / 2;

        // Calculate base price (excluding GST)
        const basePrice = gstApplicable ? discountedTotal / (1 + (gstRate / 100)) : discountedTotal;
        const cgstAmount = gstApplicable ? (basePrice * cgstRate) / 100 : 0;
        const sgstAmount = gstApplicable ? (basePrice * sgstRate) / 100 : 0;

        subtotalBeforeCashback += basePrice;
        totalCgstBeforeCashback += cgstAmount;
        totalSgstBeforeCashback += sgstAmount;
        programProductDiscount += discountAmount;
      });
    }

    // Cashback should reduce the payable amount directly without changing GST.
    const cashbackAmount = appliedCashback || 0;
    const finalSubtotal = Math.max(0, subtotalBeforeCashback - cashbackAmount);
    const finalCgst = totalCgstBeforeCashback;
    const finalSgst = totalSgstBeforeCashback;

    const totalSavings = programProductDiscount + cashbackAmount;

    // Calculate shipping charges with GST (only for product invoices)
    let shippingSubtotal = 0;
    let shippingCgstAmount = 0;
    let shippingSgstAmount = 0;
    let shippingTotal = 0;
    
    if (invoiceType === 'PRODUCT' && shippingEnabled && shippingCharges) {
      const shippingAmount = parseFloat(shippingCharges) || 0;
      if (shippingAmount > 300) {
        setFormError('Shipping charges cannot exceed ₹300');
        return;
      }
      
      // Calculate shipping GST: shippingAmount = base + 18% GST
      shippingSubtotal = gstApplicable ? shippingAmount / 1.18 : shippingAmount;
      shippingCgstAmount = gstApplicable ? shippingSubtotal * 0.09 : 0;
      shippingSgstAmount = gstApplicable ? shippingSubtotal * 0.09 : 0;
      shippingTotal = shippingAmount;
    }

    const finalTotalAmount = finalSubtotal + finalCgst + finalSgst + shippingTotal;

    setGstCalculations({
      subtotal: finalSubtotal.toFixed(2),
      totalCgst: finalCgst.toFixed(2),
      totalSgst: finalSgst.toFixed(2),
      totalDiscount: totalSavings.toFixed(2),
      totalAmount: finalTotalAmount.toFixed(2),
      subtotalBeforeCashback: subtotalBeforeCashback.toFixed(2),
      programProductDiscount: programProductDiscount.toFixed(2),
      shippingSubtotal: shippingSubtotal.toFixed(2),
      shippingCgst: shippingCgstAmount.toFixed(2),
      shippingSgst: shippingSgstAmount.toFixed(2),
      shippingTotal: shippingTotal.toFixed(2)
    });
  };

  const getProductPriceForPlan = (product, rawPlanCode) => {
    const planCode = (rawPlanCode || '').toUpperCase().replace(/[\s-]+/g, '_');

    switch (planCode) {
      case 'BRONZE':
        return product.bronzePrice;
      case 'SILVER':
        return product.silverPrice;
      case 'GOLD':
        return product.goldPrice;
      case 'SUCCESS_BUILDER':
      case 'SUCCESSBUILDER':
        return product.successBuilderPrice;
      case 'SUPERVISOR':
      case 'ASSOCIATE':
        return product.associatePrice;
      default:
        return product.mrp;
    }
  };

  const addLineItem = () => {
    if (!currentLineItem.itemId || !currentLineItem.quantity) {
      setFormError('Please select an item and enter quantity');
      return;
    }

    const itemType = currentLineItem.itemType;
    const items = itemType === 'PRODUCT' ? products : programs;
    
    // Check if items array is loaded
    if (!items || items.length === 0) {
      setFormError(`${itemType === 'PRODUCT' ? 'Products' : 'Programs'} are not loaded yet. Please wait and try again.`);
      return;
    }
    
    const selectedItem = items.find(item => item.id === parseInt(currentLineItem.itemId));
    
    if (!selectedItem) {
      setFormError('Selected item not found');
      return;
    }

    let unitPrice;
    try {
      if (itemType === 'PRODUCT') {
        const productDiscountPlanCode = customer?.productDiscountPlan?.planCode;
        unitPrice = getProductPriceForPlan(selectedItem, productDiscountPlanCode);
      } else {
        unitPrice = selectedItem.programMrp;
      }
      
      // Validate unit price
      if (!unitPrice || isNaN(unitPrice) || unitPrice <= 0) {
        setFormError('Invalid price for selected item. Please check the item configuration.');
        return;
      }
      
      const itemName = itemType === 'PRODUCT' ? selectedItem.productName : selectedItem.programName;
      const itemCode = itemType === 'PRODUCT' ? selectedItem.stockNo : selectedItem.programCode;

      const lineItem = {
        itemType: currentLineItem.itemType,
        itemId: parseInt(currentLineItem.itemId),
        itemName: itemName,
        itemCode: itemCode,
        quantity: parseInt(currentLineItem.quantity),
        unitPrice: unitPrice
      };

      setNewInvoice({
        ...newInvoice,
        lineItems: [...newInvoice.lineItems, lineItem]
      });

      setCurrentLineItem({
        itemType: invoiceType,
        itemId: '',
        quantity: 1
      });
      setFormError('');
    } catch (error) {
      console.error('Error adding line item:', error);
      setFormError('Failed to add item. Please try again.');
    }
  };

  const removeLineItem = (index) => {
    const updatedLineItems = newInvoice.lineItems.filter((_, i) => i !== index);
    setNewInvoice({
      ...newInvoice,
      lineItems: updatedLineItems
    });
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await axios.get(`/api/customers/${customerId}/invoices?page=0&size=100`);
      setInvoices(response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    if (newInvoice.lineItems.length === 0) {
      setFormError('Please add at least one line item to the invoice');
      setFormLoading(false);
      return;
    }

    try {
      const invoiceData = {
        invoiceDate: newInvoice.invoiceDate,
        dueDate: newInvoice.dueDate || null,
        description: newInvoice.description,
        status: newInvoice.status,
        paymentDate: newInvoice.paymentDate || null,
        paymentMode: newInvoice.paymentMode || null,
        paymentType: newInvoice.paymentType,
        paidAmount: newInvoice.paidAmount ? parseFloat(newInvoice.paidAmount) : null,
        notes: newInvoice.notes || null,
        cashbackAmount: appliedCashback > 0 ? appliedCashback : null,
        customDiscountAmount: (invoiceType === 'PROGRAM' && customDiscountEnabled && customDiscountAmount) ? 
          parseFloat(customDiscountAmount) : null,
        shippingCharges: (invoiceType === 'PRODUCT' && shippingEnabled && shippingCharges) ? 
          parseFloat(shippingCharges) : null,
        lineItems: newInvoice.lineItems.map(item => ({
          itemType: item.itemType,
          itemId: item.itemId,
          quantity: item.quantity
        }))
      };

      if (editingInvoice) {
        await axios.put(`/api/customers/invoices/${editingInvoice.id}`, invoiceData);
        trackEvent('affiliated_invoice_updated', {
          event_category: 'Affiliated Invoice Management',
          event_label: 'Invoice Updated',
          customer_id: customerId,
          invoice_amount: gstCalculations.totalAmount
        });
      } else {
        await axios.post(`/api/customers/${customerId}/invoices`, invoiceData);
        trackEvent('affiliated_invoice_generated', {
          event_category: 'Affiliated Invoice Management',
          event_label: 'Invoice Generated',
          customer_id: customerId,
          invoice_type: invoiceType,
          invoice_amount: gstCalculations.totalAmount,
          line_items_count: invoiceData.lineItems.length
        });
      }

      trackFormSubmission(editingInvoice ? 'edit_affiliated_invoice_form' : 'add_affiliated_invoice_form', true);
      setShowAddInvoice(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      fetchInvoices();
      fetchCashbackBalance();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setFormError(error.response?.data?.message || 'Failed to save invoice. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const resetInvoiceForm = () => {
    setNewInvoice({
      invoiceDate: getCurrentDateForInput(),
      dueDate: '',
      description: '',
      status: 'PAID',
      paymentDate: '',
      paymentMode: '',
      paymentType: 'FULL',
      paidAmount: '',
      notes: '',
      lineItems: []
    });
    setCurrentLineItem({
      itemType: 'PRODUCT',
      itemId: '',
      quantity: 1
    });
    setInvoiceType('PRODUCT');
    setAppliedCashback(0);
    setCustomDiscountEnabled(false);
    setCustomDiscountAmount('');
    setShippingEnabled(false);
    setShippingCharges('');
    setGstCalculations({
      subtotal: 0,
      totalCgst: 0,
      totalSgst: 0,
      totalDiscount: 0,
      totalAmount: 0,
      subtotalBeforeCashback: 0,
      programProductDiscount: 0,
      shippingSubtotal: 0,
      shippingCgst: 0,
      shippingSgst: 0,
      shippingTotal: 0
    });
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setNewInvoice({
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate || '',
      description: invoice.description || '',
      status: invoice.status || 'PENDING',
      paymentDate: invoice.paymentDate || '',
      paymentMode: invoice.paymentMode || '',
      paymentType: invoice.paymentType || 'FULL',
      paidAmount: invoice.paidAmount || '',
      notes: invoice.notes || '',
      lineItems: invoice.lineItems || []
    });
    setShowAddInvoice(true);
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) {
      return;
    }

    try {
      await axios.delete(`/api/customers/invoices/${invoiceId}`);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to cancel invoice:', error);
      setError('Failed to cancel invoice. Please try again.');
    }
  };

  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      trackDownload(fileName, 'pdf');
      trackEvent('affiliated_invoice_downloaded', {
        event_category: 'Affiliated Invoice Management',
        event_label: fileName,
        invoice_id: invoiceId,
        customer_id: customerId
      });

      const response = await axios.get(`/api/customers/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      setError('Failed to download invoice PDF. Please try again.');
    }
  };

  const handleReshareInvoice = async (invoice) => {
    try {
      trackEvent('affiliated_invoice_reshared_whatsapp', {
        event_category: 'Affiliated Invoice Management',
        event_label: invoice.invoiceNumber,
        invoice_id: invoice.id,
        customer_id: customerId
      });

      await axios.post(`/api/customers/invoices/${invoice.id}/reshare-whatsapp`);
      window.alert('Invoice reshared successfully over WhatsApp.');
    } catch (error) {
      console.error('Failed to reshare invoice over WhatsApp:', error);
      setError(error.response?.data || 'Failed to reshare invoice over WhatsApp. Please try again.');
    }
  };

  const handleAddPartialPayment = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setShowPartialPaymentModal(true);
  };

  const handlePartialPaymentAdded = (paymentData) => {
    fetchInvoices();
    setShowPartialPaymentModal(false);
    setSelectedInvoiceForPayment(null);
    setError('Partial payment added successfully!');
    setTimeout(() => setError(''), 3000);
  };

  const handleApplyCashback = (amount) => {
    setAppliedCashback(amount);
    setFormError('');
  };

  const handleAddProgram = async (e) => {
    e.preventDefault();
    setProgramFormLoading(true);
    setProgramFormError('');

    try {
      const programData = {
        programId: parseInt(newProgram.programId),
        startDate: newProgram.startDate,
        endDate: newProgram.endDate,
        notes: newProgram.notes || null
      };

      await axios.post(`/api/user-programs/${customerId}/assign`, programData);
      
      trackEvent('affiliated_program_assigned', {
        event_category: 'Affiliated Program Management',
        event_label: 'Program Assigned',
        customer_id: customerId,
        program_id: newProgram.programId
      });

      setShowAddProgram(false);
      resetProgramForm();
      fetchUserPrograms();
    } catch (error) {
      console.error('Failed to add program:', error);
      setProgramFormError(error.response?.data?.message || 'Failed to add program. Please try again.');
    } finally {
      setProgramFormLoading(false);
    }
  };

  const resetProgramForm = () => {
    setNewProgram({
      programId: '',
      startDate: getCurrentDateForInput(),
      endDate: '',
      notes: ''
    });
    setProgramFormError('');
  };

  const handleEndProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to end this program?')) {
      return;
    }

    try {
      await axios.put(`/api/customers/programs/${programId}/end`);
      fetchUserPrograms();
    } catch (error) {
      console.error('Failed to end program:', error);
      setError('Failed to end program. Please try again.');
    }
  };

  const handleViewProgramAttendance = async (userProgram) => {
    setSelectedProgramAttendance(userProgram);
    setShowAttendanceModal(true);
    setAttendanceLoading(true);
    
    try {
      const response = await axios.get(`/api/user-programs/${userProgram.id}/attendance`);
      setAttendanceDates(response.data);
    } catch (error) {
      console.error('Failed to fetch program attendance:', error);
      setError('Failed to load attendance data. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleStopProgram = async () => {
    if (!selectedProgramToStop) return;
    
    setStopProgramLoading(true);
    try {
      await axios.post(`/api/user-programs/${selectedProgramToStop.id}/stop`, {
        closeComments: stopProgramComments
      });
      
      trackEvent('affiliated_program_stopped', {
        event_category: 'Affiliated Program Management',
        event_label: 'Program Stopped',
        customer_id: customerId,
        program_id: selectedProgramToStop.id
      });
      
      setShowStopProgramModal(false);
      setSelectedProgramToStop(null);
      setStopProgramComments('');
      fetchUserPrograms();
      setError('Program stopped successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to stop program:', error);
      setError('Failed to stop program. Please try again.');
    } finally {
      setStopProgramLoading(false);
    }
  };

  const handleSaveProgramDates = async () => {
    if (programDetailsSaving) return;
    setProgramDetailsSaving(true); setProgramDateMessage('');
    try {
      const {data} = await axios.patch(`/api/user-programs/${selectedProgramForDetails.id}/dates`, programDates);
      setSelectedProgramForDetails(p => ({...p,...data}));
      await fetchUserPrograms(); setProgramDateMessage('Program dates saved.');
    } catch (e) { setProgramDateMessage(e.response?.data?.message || e.response?.data || 'Could not update program dates.'); }
    finally { setProgramDetailsSaving(false); }
  };

  const handleSaveProgramComments = async () => {
    if (!selectedProgramForDetails) return;

    setProgramDetailsSaving(true);
    try {
      const response = await axios.patch(`/api/user-programs/${selectedProgramForDetails.id}/comments`, {
        comments: programDetailsComments
      });

      const updatedProgram = response.data;
      setSelectedProgramForDetails(updatedProgram);
      setUserPrograms(userPrograms.map(program =>
        program.id === updatedProgram.id ? updatedProgram : program
      ));
      setError('Program comments saved successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to update program comments:', error);
      setError('Failed to save program comments. Please try again.');
    } finally {
      setProgramDetailsSaving(false);
    }
  };

  const handleDeleteProgram = async (userProgramId) => {
    if (!window.confirm('Delete this program assignment? It will be removed from the customer’s programs. Existing attendance and issued invoices will be retained. Any unissued renewal draft for this assignment will be cancelled.')) {
      return;
    }

    try {
      await axios.delete(`/api/user-programs/${userProgramId}`);
      
      trackEvent('affiliated_program_deleted', {
        event_category: 'Affiliated Program Management',
        event_label: 'Program Deleted',
        customer_id: customerId,
        program_id: userProgramId
      });
      
      fetchUserPrograms();
      setError('Program deleted successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to delete program:', error);
      setError(error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : 'Failed to delete program. Please try again.'));
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading affiliated customer details...</div>
      </div>
    );
  }

  if (error && !customer) {
    return (
      <div className="page">
        <div className="alert alert-error">{error}</div>
        <button onClick={() => navigate('/affiliated-customers')} className="btn btn-secondary">
          Back to Affiliated Customers
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <button 
            onClick={() => navigate('/affiliated-customers')} 
            className="btn btn-secondary"
            style={{ marginBottom: '10px' }}
          >
            ← Back to Affiliated Customers
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>
            {customer?.fullName || 'Affiliated Customer Details'}
          </h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--primary-blue)', 
            fontWeight: '500',
            marginTop: '5px'
          }}>
            🏢 Affiliated Customer
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Customer Details Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>Affiliated Customer Information</h3>
        </div>
        
        {customer && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Personal Details</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>Email:</strong> {customer.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Phone:</strong> {customer.phone || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Gender:</strong> {customer.gender || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Address:</strong> {customer.address || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Age:</strong> {customer.dateOfBirth ? `${calculateAgeFromDateString(customer.dateOfBirth)} years` : 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Height:</strong> {customer.heightCm ? customer.heightCm + ' cm' : 'N/A'}
              </div>
              {customer.gstNumber && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>GST Number:</strong> {customer.gstNumber}
                </div>
              )}
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Account Details</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>Status:</strong> 
                <span style={{
                  color: getStatusColor(customer.status),
                  marginLeft: '5px',
                  fontWeight: '500'
                }}>
                  {customer.status}
                </span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>PC ID/Associate ID:</strong> {customer.pcId || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Customer Type:</strong> {customer.customerType || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Referred By:</strong> {customer.referredBy?.fullName || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Joined:</strong> {customer.joinedAt ? new Date(customer.joinedAt).toLocaleDateString() : 'N/A'}
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Program Details</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>Active Program:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? activeProgram.programName : 'No Active Program';
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Checkup Day:</strong> {customer.checkupDay || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Start Date:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.startDate) : 'N/A';
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>End Date:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.endDate) : 'N/A';
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Program Discount:</strong> {customer.programDiscountPlan?.planName || 'Basic'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Product Discount:</strong> {customer.productDiscountPlan?.planName || 'Basic (MRP)'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Programs Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>Customer Programs</h3>
          <button 
            className="btn btn-primary"
            onClick={() => {
              resetProgramForm();
              setShowAddProgram(true);
            }}
          >
            Add Program
          </button>
        </div>
        
        {userProgramsLoading ? (
          <div className="loading">Loading programs...</div>
        ) : userPrograms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No programs assigned. Click "Add Program" to assign the first program.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Program</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Start Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>End Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Volume Points</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Days Attended</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Assigned By</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userPrograms.map((program, index) => (
                  <tr 
                    key={program.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      <div>{program.programName}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        {program.programCode}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDateForDisplay(program.startDate)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDateForDisplay(program.endDate)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '500' }}>
                        {program.volumePointConsumption || 0} / {program.totalVolumePoints || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        Consumption / Total
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '500' }}>
                        {program.daysAttended || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        Days Present
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: program.isActive ? 'var(--success)' : 'var(--gray-medium)',
                        color: program.isActive ? 'white' : 'var(--gray-dark)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {program.scheduled ? 'Scheduled' : program.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {program.assignedBy}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--success)', color: 'white', border: 'none' }}
	                          onClick={() => {
	                            setSelectedProgramForDetails(program);
	                            setProgramDetailsComments(program.comments || '');
                            setProgramDates({startDate:program.startDate,endDate:program.endDate});
                            setProgramDateMessage('');
	                            setShowProgramDetailsModal(true);
	                          }}
                          title="View Program Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--primary-blue)', color: 'white', border: 'none' }}
                          onClick={() => handleViewProgramAttendance(program)}
                          title="View Attendance"
                        >
                          📅
                        </button>
                        {program.isActive && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                            onClick={() => {
                              setSelectedProgramToStop(program);
                              setShowStopProgramModal(true);
                            }}
                            title="Stop Program"
                          >
                            Stop
                          </button>
                        )}
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                          onClick={() => handleDeleteProgram(program.id)}
                          title="Delete Program"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoices Section */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>Customer Invoices</h3>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingInvoice(null);
              resetInvoiceForm();
              setShowAddInvoice(true);
            }}
          >
            Add Invoice
          </button>
        </div>
        
        {invoicesLoading ? (
          <div className="loading">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No invoices found. Click "Add Invoice" to create the first invoice.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Invoice #</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Total Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Payment Info</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Due Date</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr 
                    key={invoice.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {invoice.invoiceNumber}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDateForDisplay(invoice.invoiceDate)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>{invoice.description}</div>
                      {invoice.payments && invoice.payments.length > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                          {invoice.payments.length} payment(s) made
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      <div>₹{invoice.totalAmount || invoice.amount}</div>
                      {invoice.totalPaidAmount > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--success)' }}>
                          Paid: ₹{invoice.totalPaidAmount}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontSize: '12px' }}>
                        {invoice.payments && invoice.payments.length > 0 ? (
                          <div>
                            <div>Total Paid: ₹{invoice.totalPaidAmount || 0}</div>
                            <div>Balance: ₹{invoice.remainingBalance || 0}</div>
                            <div>Last: {invoice.payments[0]?.paymentMode}</div>
                          </div>
                        ) : (
                          <div>
                            {invoice.paymentMode && <div>Mode: {invoice.paymentMode}</div>}
                            {invoice.paymentType && <div>Type: {invoice.paymentType}</div>}
                            {invoice.paidAmount && <div>Paid: ₹{invoice.paidAmount}</div>}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getInvoiceStatusColor(invoice.status),
                        fontWeight: '500'
                      }}>
                        {invoice.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {invoice.hasPdf && (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                            onClick={() => handleDownloadInvoice(invoice.id, invoice.pdfFileName || `invoice_${invoice.invoiceNumber}.pdf`)}
                            title="Download PDF"
                          >
                            📄
                          </button>
                        )}
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', background: '#25D366', color: 'white', border: 'none' }}
                          onClick={() => handleReshareInvoice(invoice)}
                          title="Reshare Invoice on WhatsApp"
                        >
                          📲
                        </button>
                        {(invoice.status === 'PENDING' || invoice.status === 'OVERDUE') && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--success)', color: 'white', border: 'none' }}
                            onClick={() => handleAddPartialPayment(invoice)}
                            title="Add Partial Payment"
                          >
                            💰
                          </button>
                        )}
                        {invoice.status !== 'CANCELLED' && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                            onClick={() => handleEditInvoice(invoice)}
                            title="Edit Invoice"
                          >
                            ✏️
                          </button>
                        )}
                        {invoice.status !== 'CANCELLED' && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                            onClick={() => handleCancelInvoice(invoice.id)}
                            title="Cancel Invoice"
                          >
                            ✖
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Invoice Modal - Same as CustomerDetails but simplified */}
      {showAddInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingInvoice ? 'Edit Invoice' : 'Generate Invoice'} - Affiliated Customer
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            {/* Company Information Section */}
            {companyInfo && (
              <div style={{
                border: '2px solid var(--light-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                background: 'var(--pale-blue)'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Company Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '5px' }}>
                      {companyInfo.companyName}
                    </div>
                    <div>{companyInfo.addressLine1}</div>
                    {companyInfo.addressLine2 && <div>{companyInfo.addressLine2}</div>}
                    <div>{companyInfo.city}, {companyInfo.state} {companyInfo.pincode}</div>
                    <div>Phone: {companyInfo.phoneNumber}</div>
                    <div>Email: {companyInfo.email}</div>
                  </div>
                  <div>
                    <div><strong>GSTIN:</strong> {companyInfo.gstinNumber || 'N/A'}</div>
                    <div><strong>FSSAI:</strong> {companyInfo.fssaiNumber || 'N/A'}</div>
                    <div style={{ marginTop: '10px' }}>
                      <strong>Invoice Date:</strong> {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Information Section */}
            <div style={{
              border: '2px solid var(--gray-medium)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              background: 'var(--gray-light)'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Bill To (Affiliated Customer)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>
                    {customer?.fullName}
                  </div>
                  <div>Email: {customer?.email}</div>
                  <div>Phone: {customer?.phone}</div>
                  {customer?.address && <div>Address: {customer.address}</div>}
                  {customer?.gstNumber && <div>GST Number: {customer.gstNumber}</div>}
                </div>
                <div>
                  <div><strong>Customer Type:</strong> {customer?.customerType}</div>
                  <div><strong>Program:</strong> {customer?.program || 'N/A'}</div>
                  {customer?.discountPlan && (
                    <div><strong>Discount Plan:</strong> {customer.discountPlan.planName}</div>
                  )}
                  <div style={{ 
                    color: 'var(--primary-blue)', 
                    fontWeight: 'bold',
                    marginTop: '10px'
                  }}>
                    🏢 Affiliated Customer
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleAddInvoice}>
              {/* Invoice Type Selection */}
              <div style={{
                border: '2px solid var(--primary-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Invoice Type</h4>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="invoiceType"
                      value="PRODUCT"
                      checked={invoiceType === 'PRODUCT'}
                      onChange={(e) => {
                        if (newInvoice.lineItems.length > 0) {
                          alert('You have items in your invoice. Please generate the invoice first or remove all items before switching invoice type.');
                          return;
                        }
                        setInvoiceType(e.target.value);
                        setCurrentLineItem({...currentLineItem, itemType: e.target.value});
                      }}
                      disabled={formLoading}
                    />
                    <span>{companyInfo?.gstinNumber?.trim() ? 'Product Invoice (5% GST)' : 'Product Invoice'}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="invoiceType"
                      value="PROGRAM"
                      checked={invoiceType === 'PROGRAM'}
                      onChange={(e) => {
                        if (newInvoice.lineItems.length > 0) {
                          alert('You have items in your invoice. Please generate the invoice first or remove all items before switching invoice type.');
                          return;
                        }
                        setInvoiceType(e.target.value);
                        setCurrentLineItem({...currentLineItem, itemType: e.target.value});
                      }}
                      disabled={formLoading}
                    />
                    <span>{companyInfo?.gstinNumber?.trim() ? 'Program Invoice (18% GST)' : 'Program Invoice'}</span>
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                  Note: You cannot mix products and programs in a single invoice
                </div>
                {!companyInfo?.gstinNumber?.trim() && (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: 'var(--gray-light)', color: 'var(--gray-dark)' }}>
                    GST will not be charged or shown because your company GSTIN is not configured.
                  </div>
                )}
                
                {/* Shipping Charges for Product Invoices */}
                {invoiceType === 'PRODUCT' && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: 'var(--pale-blue)', 
                    borderRadius: '6px',
                    border: '1px solid var(--primary-blue)'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={shippingEnabled}
                        onChange={(e) => {
                          setShippingEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setShippingCharges('');
                          }
                        }}
                        disabled={formLoading}
                      />
                      <span style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                        🚚 Include Shipping Charges
                      </span>
                    </label>
                    
                    {shippingEnabled && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label htmlFor="shippingCharges" className="form-label">
                          Shipping Charges (₹) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="300"
                          id="shippingCharges"
                          className="form-input"
                          value={shippingCharges}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            if (value > 300) {
                              setFormError('Shipping charges cannot exceed ₹300');
                              return;
                            }
                            setShippingCharges(e.target.value);
                            setFormError('');
                          }}
                          required={shippingEnabled}
                          disabled={formLoading}
                          placeholder="Enter shipping charges (max ₹300)"
                        />
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                          {companyInfo?.gstinNumber?.trim()
                            ? 'Maximum shipping charges allowed: ₹300. GST will be calculated at 18% (9% CGST + 9% SGST).'
                            : 'Maximum shipping charges allowed: ₹300. GST will not be charged.'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Custom Discount Override for Program Invoices */}
                {invoiceType === 'PROGRAM' && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: 'var(--pale-blue)', 
                    borderRadius: '6px',
                    border: '1px solid var(--primary-blue)'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={customDiscountEnabled}
                        onChange={(e) => {
                          setCustomDiscountEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setCustomDiscountAmount('');
                          }
                        }}
                        disabled={formLoading}
                      />
                      <span style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                        Override with Custom Discount
                      </span>
                    </label>
                    
                    {customDiscountEnabled && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label htmlFor="customDiscountAmount" className="form-label">
                          Custom Discount Amount (₹) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          id="customDiscountAmount"
                          className="form-input"
                          value={customDiscountAmount}
                          onChange={(e) => setCustomDiscountAmount(e.target.value)}
                          required={customDiscountEnabled}
                          disabled={formLoading}
                          placeholder="Enter custom discount amount"
                        />
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                          This will override customer's program discount slab. Items will be shown at MRP.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Line Items Section - Similar to CustomerDetails */}
              <div style={{
                border: '2px solid var(--gray-medium)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>Add Line Items</h4>
                
                {/* Add Line Item Form */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '10px', alignItems: 'end', marginBottom: '15px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">
                      {invoiceType === 'PRODUCT' ? 'Product' : 'Program'} *
                    </label>
                    <select
                      className="form-input"
                      value={currentLineItem.itemId}
                      onChange={(e) => setCurrentLineItem({...currentLineItem, itemId: e.target.value})}
                      disabled={formLoading}
                    >
                      <option value="">Select {invoiceType === 'PRODUCT' ? 'Product' : 'Program'}</option>
                      {(invoiceType === 'PRODUCT' ? products : programs.filter(p => p.programType === 'CLUB')).map(item => (
                        <option key={item.id} value={item.id}>
                          {invoiceType === 'PRODUCT' 
                            ? `${item.stockNo} - ${item.productName}`
                            : `${item.programCode} - ${item.programName}`
                          }
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      className="form-input"
                      value={currentLineItem.quantity}
                      onChange={(e) => setCurrentLineItem({...currentLineItem, quantity: e.target.value})}
                      disabled={formLoading}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addLineItem}
                      disabled={formLoading}
                      style={{ padding: '8px 16px' }}
                    >
                      Add Item
                    </button>
                  </div>
                </div>

                {/* Line Items Table */}
                {newInvoice.lineItems.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>Invoice Items</h5>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                            <th style={{ padding: '8px', textAlign: 'left', border: '1px solid var(--gray-medium)' }}>Item</th>
                            <th style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>Qty</th>
                            <th style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>Price</th>
                            <th style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>Total</th>
                            <th style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {newInvoice.lineItems.map((lineItem, index) => {
                            const unitPrice = parseFloat(lineItem.unitPrice) || 0;
                            const quantity = parseInt(lineItem.quantity) || 0;
                            const lineTotal = unitPrice * quantity;
                            
                            return (
                              <tr key={index}>
                                <td style={{ padding: '8px', border: '1px solid var(--gray-medium)' }}>
                                  <div style={{ fontWeight: 'bold' }}>{lineItem.itemName}</div>
                                  <div style={{ fontSize: '10px', color: 'var(--gray-dark)' }}>
                                    {lineItem.itemCode} • {lineItem.itemType}
                                  </div>
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>
                                  {lineItem.quantity}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>
                                  ₹{unitPrice.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)', fontWeight: 'bold' }}>
                                  ₹{lineTotal.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ fontSize: '10px', padding: '4px 8px', color: 'var(--error)' }}
                                    onClick={() => removeLineItem(index)}
                                    disabled={formLoading}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Apply Cashback Section */}
                {newInvoice.lineItems.length > 0 && cashbackBalance && cashbackBalance.availableBalance > 0 && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px',
                    background: appliedCashback > 0 ? 'linear-gradient(135deg, #C6F6D5 0%, #9AE6B4 100%)' : 'var(--pale-blue)',
                    borderRadius: '8px',
                    border: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--dark-blue)', marginBottom: '5px' }}>
                          💰 Cashback Wallet
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                          Available Balance: ₹{cashbackBalance.availableBalance.toFixed(2)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowCashbackModal(true)}
                          style={{ 
                            background: appliedCashback > 0 ? 'var(--warning)' : 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            fontSize: '14px'
                          }}
                        >
                          {appliedCashback > 0 ? '✏️ Modify Cashback' : '💰 Apply Cashback'}
                        </button>
                      </div>
                    </div>
                    
                    {appliedCashback > 0 && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: 'var(--dark-blue)',
                        border: '1px solid var(--success)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>✅ Cashback Applied:</span>
                          <span style={{ fontWeight: 'bold', fontSize: '16px', color: 'var(--success)' }}>
                            ₹{appliedCashback.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* GST Calculations Display */}
                {newInvoice.lineItems.length > 0 && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: appliedCashback > 0 ? 'linear-gradient(135deg, #C6F6D5 0%, #9AE6B4 100%)' : 'var(--white)',
                    border: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h5 style={{ color: 'var(--dark-blue)', margin: 0 }}>Invoice Calculations</h5>
                      {appliedCashback > 0 && (
                        <div style={{
                          background: 'var(--success)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          💰 Cashback Applied: ₹{appliedCashback.toFixed(2)}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '14px' }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>Subtotal (Before {customDiscountEnabled ? 'Custom Discount' : 'Cashback'}):</span>
                          <span style={{ 
                            fontWeight: 'bold',
                            color: (appliedCashback > 0 || customDiscountEnabled) ? 'var(--gray-dark)' : 'inherit',
                            textDecoration: (appliedCashback > 0 || customDiscountEnabled) ? 'line-through' : 'none'
                          }}>
                            ₹{gstCalculations.subtotalBeforeCashback}
                          </span>
                        </div>
                        {customDiscountEnabled && customDiscountAmount && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ color: 'var(--warning)', fontWeight: '500' }}>🎯 Custom Discount Applied:</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--warning)' }}>-₹{parseFloat(customDiscountAmount).toFixed(2)}</span>
                          </div>
                        )}
                        {appliedCashback > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ color: 'var(--success)', fontWeight: '500' }}>💰 Cashback Applied:</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>-₹{appliedCashback.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>Subtotal (After {customDiscountEnabled ? 'Custom Discount' : 'Cashback'}):</span>
                          <span style={{ fontWeight: 'bold', color: (appliedCashback > 0 || customDiscountEnabled) ? 'var(--success)' : 'inherit' }}>₹{gstCalculations.subtotal}</span>
                        </div>
                        {companyInfo?.gstinNumber?.trim() && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span>CGST ({invoiceType === 'PRODUCT' ? '2.5' : '9'}%):</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.totalCgst}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span>SGST ({invoiceType === 'PRODUCT' ? '2.5' : '9'}%):</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.totalSgst}</span>
                            </div>
                          </>
                        )}
                        
                        {/* Shipping Charges Section */}
                        {invoiceType === 'PRODUCT' && shippingEnabled && parseFloat(gstCalculations.shippingTotal) > 0 && (
                          <>
                            <div style={{ 
                              borderTop: '1px solid var(--gray-medium)', 
                              marginTop: '10px', 
                              paddingTop: '10px',
                              fontSize: '13px',
                              color: 'var(--primary-blue)',
                              fontWeight: 'bold'
                            }}>
                              🚚 Shipping Charges:
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                              <span>{companyInfo?.gstinNumber?.trim() ? 'Shipping (Base)' : 'Shipping Charges'}:</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingSubtotal}</span>
                            </div>
                            {companyInfo?.gstinNumber?.trim() && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                                  <span>Shipping CGST (9%):</span>
                                  <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingCgst}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                                  <span>Shipping SGST (9%):</span>
                                  <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingSgst}</span>
                                </div>
                              </>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>Total Shipping:</span>
                              <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>₹{gstCalculations.shippingTotal}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginTop: '10px',
                          paddingTop: '10px',
                          borderTop: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`,
                          fontSize: '16px'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>Final Amount:</span>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)',
                            fontSize: '18px'
                          }}>
                            ₹{Math.round(parseFloat(gstCalculations.totalAmount))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoice Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="invoiceDate" className="form-label">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    id="invoiceDate"
                    className="form-input"
                    value={newInvoice.invoiceDate}
                    onChange={(e) => setNewInvoice({...newInvoice, invoiceDate: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dueDate" className="form-label">
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    className="form-input"
                    value={newInvoice.dueDate}
                    onChange={(e) => setNewInvoice({...newInvoice, dueDate: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description *
                </label>
                <input
                  type="text"
                  id="description"
                  className="form-input"
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                  required
                  disabled={formLoading}
                  placeholder="e.g., Monthly program fee"
                />
              </div>

              {/* Payment Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="paymentType" className="form-label">
                    Payment Type *
                  </label>
                  <select
                    id="paymentType"
                    className="form-input"
                    value={newInvoice.paymentType}
                    onChange={(e) => {
                      const paymentType = e.target.value;
                      setNewInvoice({
                        ...newInvoice, 
                        paymentType: paymentType,
                        status: paymentType === 'FULL' ? 'PAID' : (paymentType === 'NONE' ? 'PENDING' : newInvoice.status)
                      });
                    }}
                    required
                    disabled={formLoading}
                  >
                    <option value="FULL">Full Payment</option>
                    <option value="PARTIAL">Partial Payment</option>
                    <option value="NONE">No Payment</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentMode" className="form-label">
                    Payment Mode
                  </label>
                  <select
                    id="paymentMode"
                    className="form-input"
                    value={newInvoice.paymentMode}
                    onChange={(e) => setNewInvoice({...newInvoice, paymentMode: e.target.value})}
                    disabled={formLoading}
                  >
                    <option value="">Select Mode</option>
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>

                {newInvoice.paymentType === 'PARTIAL' && (
                  <div className="form-group">
                    <label htmlFor="paidAmount" className="form-label">
                      Paid Amount (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="paidAmount"
                      className="form-input"
                      value={newInvoice.paidAmount}
                      onChange={(e) => setNewInvoice({...newInvoice, paidAmount: e.target.value})}
                      disabled={formLoading}
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status *
                  </label>
                  <select
                    id="status"
                    className="form-input"
                    value={newInvoice.status}
                    onChange={(e) => setNewInvoice({...newInvoice, status: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                    <option value="OVERDUE">Overdue</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentDate" className="form-label">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    id="paymentDate"
                    className="form-input"
                    value={newInvoice.paymentDate}
                    onChange={(e) => setNewInvoice({...newInvoice, paymentDate: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  className="form-input"
                  rows="3"
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                  disabled={formLoading}
                  placeholder="Additional notes..."
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddInvoice(false);
                    setEditingInvoice(null);
                    setFormError('');
                    resetInvoiceForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading || newInvoice.lineItems.length === 0}
                >
                  {formLoading ? 'Saving...' : (editingInvoice ? 'Update Invoice' : 'Generate Invoice')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Partial Payment Modal */}
      {showPartialPaymentModal && selectedInvoiceForPayment && (
        <PartialPaymentModal
          invoice={selectedInvoiceForPayment}
          onClose={() => {
            setShowPartialPaymentModal(false);
            setSelectedInvoiceForPayment(null);
          }}
          onPaymentAdded={handlePartialPaymentAdded}
        />
      )}

      {/* Cashback Modal */}
      {showCashbackModal && cashbackBalance && (
        <CashbackModal
          isOpen={showCashbackModal}
          onClose={() => setShowCashbackModal(false)}
          cashbackBalance={cashbackBalance.availableBalance}
          currentSubtotal={parseFloat(gstCalculations.subtotalBeforeCashback) || 0}
          onApplyCashback={handleApplyCashback}
          appliedCashback={appliedCashback}
        />
      )}

      {/* Add Program Modal */}
      {showAddProgram && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Assign Program - Affiliated Customer
            </h3>

            {programFormError && (
              <div className="alert alert-error">{programFormError}</div>
            )}

            <form onSubmit={handleAddProgram}>
              <div className="form-group">
                <label htmlFor="programId" className="form-label">
                  Program *
                </label>
                <select
                  id="programId"
                  className="form-input"
                  value={newProgram.programId}
                  onChange={(e) => setNewProgram({...newProgram, programId: e.target.value})}
                  required
                  disabled={programFormLoading}
                >
                  <option value="">Select Program</option>
                  {programs.filter(p => p.programType === 'CLUB').map(program => (
                    <option key={program.id} value={program.id}>
                      {program.programCode} - {program.programName} ({program.days} days)
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-input"
                    value={newProgram.startDate}
                    onChange={(e) => setNewProgram({...newProgram, startDate: e.target.value})}
                    required
                    disabled={programFormLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-input"
                    value={newProgram.endDate}
                    onChange={(e) => setNewProgram({...newProgram, endDate: e.target.value})}
                    required
                    disabled={programFormLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  className="form-input"
                  rows="3"
                  value={newProgram.notes}
                  onChange={(e) => setNewProgram({...newProgram, notes: e.target.value})}
                  disabled={programFormLoading}
                  placeholder="Additional notes about this program assignment..."
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddProgram(false);
                    setProgramFormError('');
                    resetProgramForm();
                  }}
                  disabled={programFormLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={programFormLoading}
                >
                  {programFormLoading ? 'Assigning...' : 'Assign Program'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Program Details Modal */}
      {showProgramDetailsModal && selectedProgramForDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                📋 Program Details - Affiliated Customer
              </h3>
              <button
                onClick={() => {
	                  setShowProgramDetailsModal(false);
	                  setSelectedProgramForDetails(null);
	                  setProgramDetailsComments('');
	                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              border: '2px solid var(--primary-blue)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                Program Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Program Name:</strong> {selectedProgramForDetails.programName}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Program Code:</strong> {selectedProgramForDetails.programCode}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Start Date:</strong> {formatDateForDisplay(selectedProgramForDetails.startDate)}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>End Date:</strong> {formatDateForDisplay(selectedProgramForDetails.endDate)}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Status:</strong> 
                    <span style={{
                      background: selectedProgramForDetails.isActive ? 'var(--success)' : 'var(--gray-medium)',
                      color: selectedProgramForDetails.isActive ? 'white' : 'var(--gray-dark)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {selectedProgramForDetails.scheduled ? 'Scheduled' : selectedProgramForDetails.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Assigned By:</strong> {selectedProgramForDetails.assignedBy}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Assigned Date:</strong> {selectedProgramForDetails.assignedAt ? new Date(selectedProgramForDetails.assignedAt).toLocaleDateString() : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Days Attended:</strong> {selectedProgramForDetails.daysAttended || 0}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Volume Points:</strong> {selectedProgramForDetails.volumePointConsumption || 0} / {selectedProgramForDetails.totalVolumePoints || 0}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Balance Points:</strong> {(selectedProgramForDetails.totalVolumePoints || 0) - (selectedProgramForDetails.volumePointConsumption || 0)}
                  </div>
                </div>
              </div>
            </div>

            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && <div style={{border:'1px solid #ddd',padding:16,marginBottom:20}}>
              <h4>Edit program dates</h4>
              <label>Start date<input className="form-input" type="date" value={programDates.startDate} disabled={programDetailsSaving} onChange={e=>setProgramDates(d=>({...d,startDate:e.target.value}))}/></label>
              <label>End date<input className="form-input" type="date" value={programDates.endDate} disabled={programDetailsSaving} onChange={e=>setProgramDates(d=>({...d,endDate:e.target.value}))}/></label>
              <p>Recorded attendance and guest consumption must remain within the dates. Editing dates does not reopen a stopped program.</p>
              {programDateMessage && <div role="alert">{String(programDateMessage)}</div>}
              <button className="btn btn-primary" disabled={programDetailsSaving} onClick={handleSaveProgramDates}>{programDetailsSaving?'Saving…':'Save program dates'}</button>
            </div>}
            {/* Assignment Comments */}
            {(selectedProgramForDetails.comments || !selectedProgramForDetails.isActive) && (
              <div style={{
                border: '2px solid var(--success)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
	                  📝 Assignment Comments
	                </h4>
                {!selectedProgramForDetails.isActive ? (
                  <>
                    <textarea
                      className="form-input"
                      value={programDetailsComments}
                      onChange={(e) => setProgramDetailsComments(e.target.value)}
                      rows="4"
                      disabled={programDetailsSaving}
                      placeholder="Additional comments about program assignment..."
                      style={{ resize: 'vertical', marginBottom: '12px' }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveProgramComments}
                      disabled={programDetailsSaving}
                    >
                      {programDetailsSaving ? 'Saving...' : 'Save Comments'}
                    </button>
                  </>
                ) : (
                  <div style={{
                    background: 'var(--pale-blue)',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {selectedProgramForDetails.comments}
                  </div>
                )}
              </div>
            )}

            {/* Close Comments (if program is stopped) */}
            {!selectedProgramForDetails.isActive && selectedProgramForDetails.closeComments && (
              <div style={{
                border: '2px solid var(--warning)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  🛑 Program Stop Reason
                </h4>
                <div style={{
                  background: 'var(--gray-light)',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {selectedProgramForDetails.closeComments}
                </div>
                {selectedProgramForDetails.closedAt && (
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '8px' }}>
                    Stopped on: {new Date(selectedProgramForDetails.closedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {/* Program Duration Info */}
            <div style={{
              border: '1px solid var(--gray-medium)',
              borderRadius: '8px',
              padding: '15px',
              background: 'var(--gray-light)'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                📊 Program Statistics
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                <div>
                  <strong>Total Duration:</strong> {(() => {
                    const start = new Date(selectedProgramForDetails.startDate);
                    const end = new Date(selectedProgramForDetails.endDate);
                    const diffTime = Math.abs(end - start);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return `${diffDays} days`;
                  })()}
                </div>
                <div>
                  <strong>Attendance Rate:</strong> {(() => {
                    const start = new Date(selectedProgramForDetails.startDate);
                    const end = new Date(selectedProgramForDetails.endDate);
                    const today = new Date();
                    const actualEnd = selectedProgramForDetails.isActive ? (today < end ? today : end) : end;
                    const diffTime = Math.abs(actualEnd - start);
                    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    const attendedDays = selectedProgramForDetails.daysAttended || 0;
                    const rate = totalDays > 0 ? ((attendedDays / totalDays) * 100).toFixed(1) : 0;
                    return `${rate}%`;
                  })()}
                </div>
                <div>
                  <strong>Points Utilization:</strong> {(() => {
                    const total = selectedProgramForDetails.totalVolumePoints || 0;
                    const consumed = selectedProgramForDetails.volumePointConsumption || 0;
                    const rate = total > 0 ? ((consumed / total) * 100).toFixed(1) : 0;
                    return `${rate}%`;
                  })()}
                </div>
                <div>
                  <strong>Days Remaining:</strong> {(() => {
                    if (!selectedProgramForDetails.isActive) return 'Program Completed';
                    const end = new Date(selectedProgramForDetails.endDate);
                    const today = new Date();
                    if (today > end) return 'Program Expired';
                    const diffTime = Math.abs(end - today);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return `${diffDays} days`;
                  })()}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
	                onClick={() => {
	                  setShowProgramDetailsModal(false);
	                  setSelectedProgramForDetails(null);
	                  setProgramDetailsComments('');
	                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Program Attendance Modal */}
      {showAttendanceModal && selectedProgramAttendance && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                📅 Program Attendance - Affiliated Customer
              </h3>
              <button
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedProgramAttendance(null);
                  setAttendanceDates([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                Program Details
              </h4>
              <div style={{ fontSize: '14px' }}>
                <div><strong>Program:</strong> {selectedProgramAttendance.programName}</div>
                <div><strong>Code:</strong> {selectedProgramAttendance.programCode}</div>
                <div><strong>Duration:</strong> {formatDateForDisplay(selectedProgramAttendance.startDate)} to {formatDateForDisplay(selectedProgramAttendance.endDate)}</div>
                <div><strong>Days Attended:</strong> {selectedProgramAttendance.daysAttended || 0}</div>
              </div>
            </div>

            {attendanceLoading ? (
              <div className="loading">Loading attendance data...</div>
            ) : attendanceDates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                No attendance records found for this program period.
              </div>
            ) : (
              <div>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                  Attendance Dates ({attendanceDates.length} days)
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '10px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '6px'
                }}>
                  {attendanceDates.map((date, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'var(--success)',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {formatDateForDisplay(date)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedProgramAttendance(null);
                  setAttendanceDates([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Program Modal */}
      {showStopProgramModal && selectedProgramToStop && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Stop Program - Affiliated Customer
            </h3>

            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                Program Details
              </h4>
              <div style={{ fontSize: '14px' }}>
                <div><strong>Program:</strong> {selectedProgramToStop.programName}</div>
                <div><strong>Code:</strong> {selectedProgramToStop.programCode}</div>
                <div><strong>Start Date:</strong> {formatDateForDisplay(selectedProgramToStop.startDate)}</div>
                <div><strong>End Date:</strong> {formatDateForDisplay(selectedProgramToStop.endDate)}</div>
                <div><strong>Volume Points:</strong> {selectedProgramToStop.volumePointConsumption || 0} / {selectedProgramToStop.totalVolumePoints || 0}</div>
                <div><strong>Balance Volume Points:</strong> {(selectedProgramToStop.totalVolumePoints || 0) - (selectedProgramToStop.volumePointConsumption || 0)}</div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="stopComments" className="form-label">
                Reason for Stopping Program *
              </label>
              <textarea
                id="stopComments"
                className="form-input"
                rows="3"
                value={stopProgramComments}
                onChange={(e) => setStopProgramComments(e.target.value)}
                required
                disabled={stopProgramLoading}
                placeholder="Please provide reason for stopping this program..."
              />
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowStopProgramModal(false);
                  setSelectedProgramToStop(null);
                  setStopProgramComments('');
                }}
                disabled={stopProgramLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStopProgram}
                disabled={stopProgramLoading || !stopProgramComments.trim()}
                style={{ 
                  background: 'var(--error)',
                  border: 'none'
                }}
              >
                {stopProgramLoading ? 'Stopping...' : 'Stop Program'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatedCustomerDetails;
